import React from 'react';
import LandingHeader from '../../ui/LandingHeader';
import LandingFooter from '../../ui/LandingFooter';

const ForGovernmentsPage: React.FC = () => {
    return (
        <div className="bg-background min-h-screen">
            <div className="relative z-10">
                <LandingHeader />
            </div>
            
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-gradient-to-b from-muted/50 to-background pt-24 pb-16">
                <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
                <div className="container relative mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <span className="inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium bg-muted/50 text-muted-foreground mb-6 backdrop-blur-sm">
                            For Governments
                        </span>
                        <h1 className="text-5xl font-bold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/70">
                            Build Secure, Interactive Tools, Entirely Local
                        </h1>
                        <p className="text-xl text-muted-foreground mb-8">
                            Create and share fully self-contained applications using Python and structured data. No internet access, cloud services, or runtime permissions required.
                        </p>
                        <div className="flex justify-center gap-4 text-lg font-semibold">
                            <span className="text-primary">Zero Backend</span>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-primary">Zero Risk</span>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-primary">100% Local</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Local-First Matters */}
            <section className="py-16 relative">
                <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
                <div className="container relative mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">Why Local-First Matters for Government</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">No Cloud Required</h3>
                                <p className="text-muted-foreground">Everything runs client-side, making it ideal for air-gapped and classified environments.</p>
                            </div>
                        </div>
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Deploy Anywhere</h3>
                                <p className="text-muted-foreground">From internal servers to USB keys to intranets. Zero dependencies.</p>
                            </div>
                        </div>
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Self-Contained Artifacts</h3>
                                <p className="text-muted-foreground">One folder contains all code, UI, data, and logic, making it simple to audit and distribute.</p>
                            </div>
                        </div>
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">No Runtime Risk</h3>
                                <p className="text-muted-foreground">No server to patch, no long-term hosting commitments.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Use Case Examples */}
            <section className="py-16 bg-muted/50 relative">
                <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
                <div className="container relative mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">High-Trust, Low-Infrastructure Use Cases</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Crisis Response Tools</h3>
                                <p className="text-muted-foreground">Shareable decision support models with offline access.</p>
                            </div>
                        </div>
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Local Government Budget Portals</h3>
                                <p className="text-muted-foreground">Finance teams generate interactive fiscal dashboards without requiring IT support.</p>
                            </div>
                        </div>
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Public Datasets</h3>
                                <p className="text-muted-foreground">Publish browsable, filterable data with full interactivity, no APIs.</p>
                            </div>
                        </div>
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Internal Playbooks & Audits</h3>
                                <p className="text-muted-foreground">Program staff build scenario explorers and SOPs with no cloud footprint.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Key Benefits */}
            <section className="py-16 relative">
                <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
                <div className="container relative mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h2 className="text-2xl font-bold mb-6 group-hover:text-primary transition-colors">Operational Autonomy</h2>
                                <ul className="space-y-4">
                                    <li className="flex items-start">
                                        <span className="text-primary mr-2">✓</span>
                                        <span>Empower domain experts to build and share without engineering bottlenecks</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-primary mr-2">✓</span>
                                        <span>Iterate quickly: develop, preview, and export on one machine</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-primary mr-2">✓</span>
                                        <span>Preserve control over tools and logic</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h2 className="text-2xl font-bold mb-6 group-hover:text-primary transition-colors">Security & Compliance</h2>
                                <ul className="space-y-4">
                                    <li className="flex items-start">
                                        <span className="text-primary mr-2">✓</span>
                                        <span>Fully offline apps with no third-party tracking or runtime code execution</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-primary mr-2">✓</span>
                                        <span>All logic visible in plain Python, making it easy to version and audit</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-primary mr-2">✓</span>
                                        <span>Ideal for use in secure enclaves and regulatory environments</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Local-First Workflow */}
            <section className="py-16 bg-muted/50 relative">
                <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
                <div className="container relative mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl font-bold mb-8">Local-First Workflow</h2>
                        <div className="flex justify-center items-center gap-4 text-lg font-medium mb-4">
                            <span>Preswald Init</span>
                            <span className="text-muted-foreground">→</span>
                            <span>Develop in Python</span>
                            <span className="text-muted-foreground">→</span>
                            <span>Export to Static Folder</span>
                            <span className="text-muted-foreground">→</span>
                            <span>Run Locally or Distribute</span>
                        </div>
                        <p className="text-muted-foreground">Your app lives in a folder, not a server.</p>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-16 relative">
                <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
                <div className="container relative mx-auto px-4 text-center">
                    <h2 className="text-3xl font-bold mb-8">Ready to Transform Your Government Workflows?</h2>
                    <div className="flex justify-center">
                        <button 
                            onClick={() => window.open('https://cal.com/amruthagujjar/30min', '_blank')}
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-8"
                        >
                            Book a Demo
                        </button>
                    </div>
                </div>
            </section>

            <LandingFooter />
        </div>
    );
};

export default ForGovernmentsPage; 